/**
 * CL-ACCESS-FINANCIALS-001B — Staff Financials tab + subtabs (gate-on path only).
 */

import {
  clientPortalDesignBoardsTabAllowed,
  clientPortalSpecificationsTabAllowed,
  clientPortalTradesSuppliersTabAllowed,
} from "./clientPortalProjectOperatorAccessModel";
import { PROJECT_TABS, PROJECT_TAB_LABELS } from "./projectPortalTabsModel";
import { projectAccountingPermissionsUiEnabled } from "./projectAccountingPermissionsFeatureGate";
import { evaluateStaffFinancialCapabilities } from "./projectStaffFinancialAuthorizationModel";
import {
  assignmentUsesLegacyInvoiceFallback,
} from "./projectInvoicePermissionsEditorModel";
import {
  assignmentShowsProjectIdentityHeaderOnly,
  resolveSectionAccessMap,
} from "./projectCollaboratorAccessResolutionModel";

function isCustomAccessScopeValue(accessScope) {
  return String(accessScope ?? "").trim().toLowerCase() === "custom";
}

export const STAFF_FINANCIAL_SUBTAB_KEYS = Object.freeze({
  OVERVIEW: "overview",
  BUDGET: "budget",
  INVOICES: "invoices",
  TIME_LOG: "time_log",
  RECEIPTS: "receipts",
  PAYMENTS: "payments",
});

export const STAFF_FINANCIAL_SUBTAB_LABELS = Object.freeze({
  [STAFF_FINANCIAL_SUBTAB_KEYS.OVERVIEW]: "Overview",
  [STAFF_FINANCIAL_SUBTAB_KEYS.BUDGET]: "Budget",
  [STAFF_FINANCIAL_SUBTAB_KEYS.INVOICES]: "Invoices",
  [STAFF_FINANCIAL_SUBTAB_KEYS.TIME_LOG]: "Time Log",
  [STAFF_FINANCIAL_SUBTAB_KEYS.RECEIPTS]: "Receipts",
  [STAFF_FINANCIAL_SUBTAB_KEYS.PAYMENTS]: "Payments",
});

const FIRST_RELEASE_SUBTABS = new Set([
  STAFF_FINANCIAL_SUBTAB_KEYS.OVERVIEW,
  STAFF_FINANCIAL_SUBTAB_KEYS.BUDGET,
  STAFF_FINANCIAL_SUBTAB_KEYS.INVOICES,
  STAFF_FINANCIAL_SUBTAB_KEYS.TIME_LOG,
]);

const EXPLICIT_STAFF_FINANCIAL_PERMISSION_KEYS = Object.freeze([
  "canViewInvoices",
  "canViewBudget",
  "canViewTimeLog",
  "canManageBudget",
  "canManageTimeEntries",
  "canPrepareInvoices",
  "canReviewInvoices",
  "canApproveInvoices",
  "canShareClientTest",
  "canIssueInvoices",
  "canManageInvoiceSettings",
  "canViewReceipts",
  "canManageReceipts",
  "canViewPayments",
  "canRecordPayments",
  "canAccessFinancials",
  "canAccessTimeLog",
]);

export function hasExplicitStaffFinancialPermissionKeys(permissions = {}) {
  return EXPLICIT_STAFF_FINANCIAL_PERMISSION_KEYS.some(
    (key) => typeof permissions?.[key] === "boolean"
  );
}

/**
 * Gate-on staff Financials portal tab model (includes custom scope without financial caps).
 */
export function assignmentUsesStaffFinancialPortalTabModel({
  assignment = {},
  accountingPermissionsUiEnabled = null,
} = {}) {
  if (!projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    return false;
  }
  if (isCustomAccessScopeValue(assignment.accessScope)) {
    return true;
  }
  return assignmentUsesStaffFinancialPortal({ assignment, accountingPermissionsUiEnabled });
}

/**
 * Gate-on staff Financials portal content (Financials tab body + subtabs) — requires Financials access.
 */
export function assignmentUsesStaffFinancialPortal({
  assignment = {},
  accountingPermissionsUiEnabled = null,
} = {}) {
  if (!projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    return false;
  }

  const caps = evaluateStaffFinancialCapabilities({ assignment, accountingPermissionsUiEnabled });
  if (caps.canAccessFinancials !== true) {
    return false;
  }

  if (isCustomAccessScopeValue(assignment.accessScope)) {
    return true;
  }

  const permissions = assignment.permissions || {};
  if (assignmentUsesLegacyInvoiceFallback(permissions)) {
    return false;
  }

  return hasExplicitStaffFinancialPermissionKeys(permissions);
}

export function buildStaffFinancialSubtabs({
  assignment = {},
  accountingPermissionsUiEnabled = null,
  includeReservedModules = false,
} = {}) {
  if (!projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    return [];
  }

  const caps = evaluateStaffFinancialCapabilities({ assignment, accountingPermissionsUiEnabled });
  const subtabs = [];

  if (caps.canAccessFinancials === true) {
    subtabs.push({
      key: STAFF_FINANCIAL_SUBTAB_KEYS.OVERVIEW,
      label: STAFF_FINANCIAL_SUBTAB_LABELS[STAFF_FINANCIAL_SUBTAB_KEYS.OVERVIEW],
    });
  }

  if (caps.canViewBudget || caps.canManageBudget) {
    subtabs.push({
      key: STAFF_FINANCIAL_SUBTAB_KEYS.BUDGET,
      label: STAFF_FINANCIAL_SUBTAB_LABELS[STAFF_FINANCIAL_SUBTAB_KEYS.BUDGET],
    });
  }

  if (
    caps.canViewInvoices ||
    caps.canPrepareInvoices ||
    caps.canReviewInvoices ||
    caps.canApproveInvoices ||
    caps.canShareClientTest ||
    caps.canIssueInvoices
  ) {
    subtabs.push({
      key: STAFF_FINANCIAL_SUBTAB_KEYS.INVOICES,
      label: STAFF_FINANCIAL_SUBTAB_LABELS[STAFF_FINANCIAL_SUBTAB_KEYS.INVOICES],
    });
  }

  if (caps.canViewTimeLog || caps.canManageTimeEntries) {
    subtabs.push({
      key: STAFF_FINANCIAL_SUBTAB_KEYS.TIME_LOG,
      label: STAFF_FINANCIAL_SUBTAB_LABELS[STAFF_FINANCIAL_SUBTAB_KEYS.TIME_LOG],
    });
  }

  if (includeReservedModules) {
    if (caps.canViewReceipts || caps.canManageReceipts) {
      subtabs.push({
        key: STAFF_FINANCIAL_SUBTAB_KEYS.RECEIPTS,
        label: STAFF_FINANCIAL_SUBTAB_LABELS[STAFF_FINANCIAL_SUBTAB_KEYS.RECEIPTS],
      });
    }
    if (caps.canViewPayments || caps.canRecordPayments) {
      subtabs.push({
        key: STAFF_FINANCIAL_SUBTAB_KEYS.PAYMENTS,
        label: STAFF_FINANCIAL_SUBTAB_LABELS[STAFF_FINANCIAL_SUBTAB_KEYS.PAYMENTS],
      });
    }
  }

  return subtabs.filter((tab) => FIRST_RELEASE_SUBTABS.has(tab.key) || includeReservedModules);
}

export function buildStaffPortalTabsWhenAccountingEnabled({
  permissions = {},
  project = null,
  assignment = null,
  accountingPermissionsUiEnabled = true,
  isPastProject = false,
  isSharedFilesOnlyViewer = false,
  packageAvailable = false,
  isClientPersona = false,
  isContributorPersona = false,
  hasAuthorizedRenderings = false,
} = {}) {
  const resolvedAssignment = assignment || {
    permissions,
    accessScope: "full_project",
    status: "active",
  };

  const caps = evaluateStaffFinancialCapabilities({
    assignment: resolvedAssignment,
    accountingPermissionsUiEnabled,
  });
  const sections = resolveSectionAccessMap({
    rawPermissions: resolvedAssignment.permissions || permissions,
    accessScope: resolvedAssignment.accessScope || "full_project",
    role: resolvedAssignment.role || "collaborator",
    accountingPermissionsUiEnabled,
  });

  const tabs = [];

  if (sections.canAccessOverview) {
    tabs.push({ key: PROJECT_TABS.OVERVIEW, label: PROJECT_TAB_LABELS[PROJECT_TABS.OVERVIEW] });
  } else if (
    assignmentShowsProjectIdentityHeaderOnly({
      rawPermissions: resolvedAssignment.permissions || permissions,
      accessScope: resolvedAssignment.accessScope,
      role: resolvedAssignment.role,
      accountingPermissionsUiEnabled,
    })
  ) {
    tabs.push({ key: PROJECT_TABS.OVERVIEW, label: PROJECT_TAB_LABELS[PROJECT_TABS.OVERVIEW], headerOnly: true });
  }

  if (caps.canAccessFinancials) {
    tabs.push({ key: PROJECT_TABS.FINANCIALS, label: PROJECT_TAB_LABELS[PROJECT_TABS.FINANCIALS] });
  }

  if (sections.canAccessPlanning && permissions.canViewPlanning) {
    tabs.push({ key: PROJECT_TABS.PLANNING, label: PROJECT_TAB_LABELS[PROJECT_TABS.PLANNING] });
  }

  if (sections.canAccessReview && permissions.canReviewDrawings) {
    tabs.push({
      key: PROJECT_TABS.DRAWINGS_REVIEW,
      label: PROJECT_TAB_LABELS[PROJECT_TABS.DRAWINGS_REVIEW],
    });
  }

  if (sections.canAccessTimeLog && !caps.canAccessFinancials) {
    if (caps.canViewTimeLog || caps.canManageTimeEntries) {
      tabs.push({ key: PROJECT_TABS.TIME_LOG, label: PROJECT_TAB_LABELS[PROJECT_TABS.TIME_LOG] });
    }
  }

  const operatorPermissions = resolvedAssignment.permissions || permissions;
  if (
    clientPortalTradesSuppliersTabAllowed(operatorPermissions)
    && !tabs.some((item) => item.key === PROJECT_TABS.TRADES_SUPPLIERS)
  ) {
    tabs.push({
      key: PROJECT_TABS.TRADES_SUPPLIERS,
      label: PROJECT_TAB_LABELS[PROJECT_TABS.TRADES_SUPPLIERS],
    });
  }

  if (
    clientPortalSpecificationsTabAllowed(operatorPermissions, resolvedAssignment)
    && !tabs.some((item) => item.key === PROJECT_TABS.SPECIFICATIONS)
  ) {
    tabs.push({
      key: PROJECT_TABS.SPECIFICATIONS,
      label: PROJECT_TAB_LABELS[PROJECT_TABS.SPECIFICATIONS],
    });
  }

  if (
    clientPortalDesignBoardsTabAllowed(operatorPermissions, resolvedAssignment)
    && !tabs.some((item) => item.key === PROJECT_TABS.DESIGN_BOARDS)
  ) {
    tabs.push({
      key: PROJECT_TABS.DESIGN_BOARDS,
      label: PROJECT_TAB_LABELS[PROJECT_TABS.DESIGN_BOARDS],
    });
  }

  return tabs;
}

export function listCollaboratorAccessScopeOptions({ accountingPermissionsUiEnabled = null } = {}) {
  const base = ["full_project", "shared_files", "concept_only", "permit_drawings"];
  if (!projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    return base;
  }
  return [...base, "custom"];
}

export function listCollaboratorPermissionPresetOptions({ accountingPermissionsUiEnabled = null } = {}) {
  const base = ["client", "estimator", "designer", "trade", "project_admin", "custom"];
  if (!projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    return base;
  }
  return [...base, "accounting"];
}
